import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ConfirmModal } from '../components/ConfirmModal';
import { useUrlState } from '../hooks/useUrlState';
import { apiClient } from '../lib/api-client';
import { formatDateTime } from '../utils/datetime';

type Currency = {
  id: string;
  code: string;
  name: string;
  symbol: string;
  useDecimals: boolean;
  decimalPlaces: number;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

// URL に不正値が入った場合のフォールバック用の許可値。
const CURRENCY_FILTERS = ['all', 'active', 'inactive'] as const;

export default function ServiceSettingsPage() {
  const [settings, setSettings] = useState<{
    id: number;
    is_closed_beta: boolean;
    is_service_suspended: boolean;
    updated_at: string;
  } | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // 通貨フィルタは URL クエリで管理し、戻る / 進む と URL 共有に対応する
  const [{ currencyFilter }, setUrlState] = useUrlState(
    { currencyFilter: 'active' },
    { currencyFilter: CURRENCY_FILTERS }
  );
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    field: 'is_closed_beta' | 'is_service_suspended' | null;
  }>({
    isOpen: false,
    title: '',
    message: '',
    field: null
  });

  useEffect(() => {
    void loadSettings();
    void loadCurrencies();
  }, [currencyFilter]);

  const loadSettings = async () => {
    try {
      const data = await apiClient.getServiceSettings();
      setSettings(data);
    } catch (_err) {
      setError('Failed to load service settings');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrencies = async () => {
    try {
      // Always include inactive currencies to support all filter options
      const includeInactive = currencyFilter !== 'active';
      const data = await apiClient.getCurrencies(includeInactive);
      setCurrencies(data.currencies);
    } catch (err) {
      console.error('Failed to load currencies:', err);
    }
  };

  const getConfirmationMessage = (
    field: 'is_closed_beta' | 'is_service_suspended',
    currentValue: boolean
  ) => {
    if (field === 'is_closed_beta') {
      if (currentValue) {
        return 'Disable closed beta mode?\n\nAll users will be able to access the service without access keywords.\n\nContinue?';
      } else {
        return 'Enable closed beta mode?\n\nOnly users with access keywords will be able to use the service.\nExisting users will not be affected.\n\nContinue?';
      }
    } else {
      if (currentValue) {
        return 'Resume service?\n\nThe service will return to normal operation and all users will be able to access it.\n\nContinue?';
      } else {
        return 'Suspend service?\n\nAll users will be unable to access the service.\nScheduled posts will not be executed.\n\nAre you sure you want to continue?';
      }
    }
  };

  const handleToggle = (field: 'is_closed_beta' | 'is_service_suspended') => {
    if (!settings) {
      return;
    }

    const message = getConfirmationMessage(field, settings[field]);
    const title = field === 'is_closed_beta' ? 'Change Closed Beta Mode' : 'Change Service Status';

    setConfirmModal({
      isOpen: true,
      title,
      message,
      field
    });
  };

  const handleConfirmToggle = async () => {
    const field = confirmModal.field;
    if (!settings || !field) {
      return;
    }

    setConfirmModal({ isOpen: false, title: '', message: '', field: null });
    setSaving(true);
    setError('');

    try {
      const updated = await apiClient.updateServiceSettings({
        [field]: !settings[field]
      });
      setSettings(updated);
    } catch (_err) {
      setError('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelToggle = () => {
    setConfirmModal({ isOpen: false, title: '', message: '', field: null });
  };

  const handleToggleCurrencyActive = async (currencyId: string, isActive: boolean) => {
    try {
      if (isActive) {
        await apiClient.deleteCurrency(currencyId);
      } else {
        await apiClient.activateCurrency(currencyId);
      }
      await loadCurrencies();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to toggle currency status');
    }
  };

  const filteredCurrencies = currencies.filter(currency => {
    if (currencyFilter === 'all') return true;
    if (currencyFilter === 'active') return currency.isActive;
    if (currencyFilter === 'inactive') return !currency.isActive;
    return true;
  });

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className='space-y-8'>
      <h1 className='mb-6 text-3xl font-bold text-gray-800 dark:text-white'>Service Settings</h1>

      {error && (
        <div className='rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-950 dark:text-red-200'>
          {error}
        </div>
      )}

      {/* Service Configuration Section */}
      <div className='rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
        <h2 className='mb-4 text-xl font-semibold text-gray-700 dark:text-white'>
          Service Configuration
        </h2>

        <div className='space-y-4'>
          <div className='flex items-center justify-between border-b pb-4'>
            <div>
              <h3 className='font-medium text-gray-800 dark:text-white'>Closed Beta Mode</h3>
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                Restrict access to users with access keywords
              </p>
            </div>
            <button
              onClick={() => handleToggle('is_closed_beta')}
              disabled={saving}
              className={`rounded-lg px-4 py-2 font-semibold text-white transition-colors dark:text-gray-900 ${
                settings?.is_closed_beta
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-gray-400 hover:bg-gray-500'
              } disabled:bg-gray-300`}
            >
              {settings?.is_closed_beta ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          <div className='flex items-center justify-between'>
            <div>
              <h3 className='font-medium text-gray-800 dark:text-white'>Service Suspension</h3>
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                Suspend the entire service temporarily
              </p>
            </div>
            <button
              onClick={() => handleToggle('is_service_suspended')}
              disabled={saving}
              className={`rounded-lg px-4 py-2 font-semibold text-white transition-colors dark:text-gray-900 ${
                settings?.is_service_suspended
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-gray-400 hover:bg-gray-500'
              } disabled:bg-gray-300`}
            >
              {settings?.is_service_suspended ? 'Suspended' : 'Active'}
            </button>
          </div>
        </div>

        {settings && (
          <div className='mt-6 text-sm text-gray-500 dark:text-gray-400'>
            Last updated:{' '}
            {formatDateTime(settings.updated_at, { dateStyle: 'medium', timeStyle: 'short' }) ||
              'N/A'}
          </div>
        )}
      </div>

      {/* Currency Management Section */}
      <a id='currencies'></a>
      <div className='rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
        <div className='mb-6 flex items-center justify-between'>
          <h2 className='text-xl font-semibold text-gray-700 dark:text-gray-300'>
            Currency Management
          </h2>
          <Link
            to='/settings/currencies/create'
            className='rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700'
          >
            Add
          </Link>
        </div>

        {/* Currency Filters */}
        <div className='mb-4 flex gap-2'>
          <button
            onClick={() => setUrlState({ currencyFilter: 'all' })}
            className={`rounded-md px-4 py-2 ${
              currencyFilter === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All ({currencies.length})
          </button>
          <button
            onClick={() => setUrlState({ currencyFilter: 'active' })}
            className={`rounded-md px-4 py-2 ${
              currencyFilter === 'active'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Active ({currencies.filter(c => c.isActive).length})
          </button>
          <button
            onClick={() => setUrlState({ currencyFilter: 'inactive' })}
            className={`rounded-md px-4 py-2 ${
              currencyFilter === 'inactive'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Inactive ({currencies.filter(c => !c.isActive).length})
          </button>
        </div>

        {/* Currency Table */}
        <div className='overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700'>
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
              <thead className='bg-gray-50 dark:bg-gray-800'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    Code
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    Name
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    Symbol
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    Decimals
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    Display Order
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    Status
                  </th>
                  <th className='px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900'>
                {filteredCurrencies.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className='px-6 py-4 text-center text-gray-500 dark:text-gray-400'
                    >
                      No currencies found
                    </td>
                  </tr>
                ) : (
                  filteredCurrencies.map(currency => (
                    <tr
                      key={currency.id}
                      className='transition-colors hover:bg-gray-50 hover:dark:bg-gray-800'
                    >
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span className='font-mono text-sm font-semibold text-gray-900 uppercase dark:text-gray-100'>
                          {currency.code}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span className='text-sm text-gray-900 dark:text-gray-100'>
                          {currency.name}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span className='text-lg font-semibold text-gray-700 dark:text-gray-300'>
                          {currency.symbol}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span className='text-sm text-gray-600'>
                          {currency.useDecimals
                            ? `${currency.decimalPlaces} digits`
                            : 'No decimals'}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span className='text-sm text-gray-600 dark:text-gray-400'>
                          {currency.displayOrder}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className={`rounded px-2 py-1 text-xs font-semibold ${
                            currency.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                          }`}
                        >
                          {currency.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className='px-6 py-4 text-right text-sm font-medium whitespace-nowrap'>
                        <div className='flex justify-end gap-2'>
                          <Link
                            to={`/settings/currencies/${currency.id}/edit`}
                            className='text-blue-600 transition-colors hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300'
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() =>
                              handleToggleCurrencyActive(currency.id, currency.isActive)
                            }
                            className={`${
                              currency.isActive
                                ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                                : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                            }`}
                          >
                            {currency.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText='Continue'
        cancelText='Cancel'
        onConfirm={handleConfirmToggle}
        onCancel={handleCancelToggle}
        variant={confirmModal.field === 'is_service_suspended' ? 'danger' : 'default'}
      />
    </div>
  );
}
